<template>
  <div id="app">
    <app-detail-header :title="'Assign ORCID Units'"></app-detail-header>
    <b-container>
      <b-row>
        <b-col lg="10" xl="8">
          <b-card
            title="Assign Units to ORCID"
            class="mt-3"
            header-tag="header"
            variant="primary"
          >
            <p class="text-muted mb-3">
              Add usage units to a user's account by their ORCID ID. A transaction record will be created automatically.
            </p>

            <b-form @submit.prevent="onSubmit">
              <b-form-group>
                <b-row>
                  <b-col md="8">
                    <b-form-group label="ORCID ID" label-class="font-weight-bold">
                      <b-form-input
                        v-model="formData.orcidId"
                        placeholder="e.g. 0000-0002-9997-6489"
                        required
                        trim
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                  <b-col md="4">
                    <b-form-group label="Units" label-class="font-weight-bold">
                      <b-form-input
                        v-model.number="formData.units"
                        type="number"
                        min="1"
                        placeholder="e.g. 300"
                        required
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-form-group>

              <b-form-group>
                <b-row>
                  <b-col md="6">
                    <b-form-group label="Transaction Type" label-class="font-weight-bold">
                      <b-form-select
                        v-model="formData.transactionType"
                        :options="transactionTypeOptions"
                        required
                      ></b-form-select>
                    </b-form-group>
                  </b-col>
                  <b-col md="6">
                    <b-form-group label="Invoice / Request Info" label-class="font-weight-bold">
                      <b-form-input
                        v-model="formData.invoiceInfo"
                        placeholder="e.g. Invoice 4417 paid"
                        required
                        trim
                      ></b-form-input>
                    </b-form-group>
                  </b-col>
                </b-row>
              </b-form-group>

              <b-button type="submit" variant="primary" :disabled="submitting">
                <b-spinner small v-if="submitting" class="mr-2"></b-spinner>
                {{ submitting ? "Assigning..." : "Assign Units" }}
              </b-button>
              <b-button
                type="button"
                variant="outline-secondary"
                class="ml-2"
                @click="resetForm"
                :disabled="submitting"
              >
                Reset
              </b-button>
            </b-form>

            <!-- Error -->
            <b-alert
              v-if="assignmentResult && !assignmentResult.success"
              variant="danger"
              show
              class="mt-3"
            >
              <strong>Error:</strong> {{ assignmentResult.error }}
            </b-alert>

            <!-- Success -->
            <div v-if="assignmentResult && assignmentResult.success" class="mt-4">
              <b-alert variant="success" :show="true">
                <strong>Success!</strong>
                Units {{ assignmentResult.action === 'created' ? 'assigned — new bucket created' : 'added to existing bucket' }}
                for <strong>{{ assignmentResult.email || assignmentResult.orcidId }}</strong>.
              </b-alert>

              <h6 class="font-weight-bold mb-2">Assignment Details</h6>
              <table class="table table-sm table-bordered">
                <tbody>
                  <tr>
                    <td class="font-weight-bold text-nowrap" style="width: 40%">ORCID ID</td>
                    <td>{{ assignmentResult.orcidId }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Email</td>
                    <td>{{ assignmentResult.email || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Username</td>
                    <td>{{ assignmentResult.username || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Units Added</td>
                    <td class="text-success font-weight-bold">+{{ assignmentResult.unitsAdded }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">Previous Total / Remaining</td>
                    <td>{{ assignmentResult.prevTotalUnits || 0 }} / {{ assignmentResult.prevRemainingUnits || 0 }}</td>
                  </tr>
                  <tr>
                    <td class="font-weight-bold">New Total / Remaining</td>
                    <td class="font-weight-bold">{{ assignmentResult.newTotalUnits }} / {{ assignmentResult.newRemainingUnits }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </b-card>
        </b-col>
      </b-row>
    </b-container>
  </div>
</template>

<script>
import { mapActions } from "vuex";

export default {
  name: "OrcidUnitAssignment",
  beforeRouteEnter(to, from, next) {
    const user = JSON.parse(localStorage.getItem("user"));
    if (user && user.role === "Admin") {
      next();
    } else {
      next("/main/dashboard/sales");
    }
  },
  data() {
    return {
      formData: {
        orcidId: "",
        units: null,
        invoiceInfo: "",
        transactionType: null,
      },
      transactionTypeOptions: [
        { text: "Select Transaction Type", value: null, disabled: true },
        { text: "invoice", value: "invoice" },
        { text: "free_teaching", value: "free_teaching" },
      ],
      submitting: false,
      assignmentResult: null,
    };
  },
  methods: {
    ...mapActions("usage", ["assignUnitsToOrcid"]),
    resetForm() {
      this.formData = {
        orcidId: "",
        units: null,
        invoiceInfo: "",
        transactionType: null,
      };
      this.assignmentResult = null;
    },
    async onSubmit() {
      this.submitting = true;
      this.assignmentResult = null;

      try {
        const payload = {
          orcidId: this.formData.orcidId,
          units: this.formData.units,
          invoiceInfo: this.formData.invoiceInfo,
          transactionType: this.formData.transactionType,
          bucketTypeId: 10,
          partnerId: "tair",
        };

        const response = await this.assignUnitsToOrcid(payload);
        this.assignmentResult = response;
      } catch (error) {
        this.assignmentResult = {
          success: false,
          error: error.error || error.message || "An error occurred while assigning units",
        };
      } finally {
        this.submitting = false;
      }
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/views/usage/OrcidUnitAssignment.vue